import React, { useState, useEffect, useRef, useCallback } from 'react';
import './EndlessMode.css';

import Settings from '../components/common/settings';
import Lost from '../components/common/lost';
import { useNavigate } from 'react-router-dom';
import lockedSound from '../audios/mixkit-select-click-1109.wav';
import settingsSound from '../audios/settoings.wav';

import keyImg from '../assets/keyyy.png';
import candleImg from '../assets/candle.webp';
import teacupImg from '../assets/teacup.png';
import watchImg from '../assets/WATCHH.png';
import bookImg from '../assets/boook.png';
import photoImg from '../assets/photo.png';
import lanternImg from '../assets/lantern.webp';
import bottleImg from '../assets/bottlee.png';
import bellImg from '../assets/bell.png';
import maskImg from '../assets/mask.webp';
import featherImg from '../assets/feather.png';
import compassImg from '../assets/compass.png';
import scissorsImg from '../assets/scissors.webp';
import dollImg from '../assets/doll.png';
import suitcaseImg from '../assets/suitcase.webp';
import coinImg from '../assets/coin.png';
import clockImg from '../assets/clock.png';
import gloveImg from '../assets/glove.png';
import diaryImg from '../assets/diary.png';
import ringImg from '../assets/ring.png';

const OBJECTS = [
  { id: 'key', name: 'Key', img: keyImg },
  { id: 'candle', name: 'Candle', img: candleImg },
  { id: 'teacup', name: 'Teacup', img: teacupImg },
  { id: 'watch', name: 'Pocket Watch', img: watchImg },
  { id: 'book', name: 'Book', img: bookImg },
  { id: 'photo', name: 'Photograph', img: photoImg },
  { id: 'lantern', name: 'Lantern', img: lanternImg },
  { id: 'bottle', name: 'Wine Bottle', img: bottleImg },
  { id: 'bell', name: 'Bell', img: bellImg },
  { id: 'mask', name: 'Mask', img: maskImg },
  { id: 'feather', name: 'Feather', img: featherImg },
  { id: 'compass', name: 'Compass', img: compassImg },
  { id: 'scissors', name: 'Scissors', img: scissorsImg },
  { id: 'doll', name: 'Doll', img: dollImg },
  { id: 'suitcase', name: 'Suitcase', img: suitcaseImg },
  { id: 'coin', name: 'Coin', img: coinImg },
  { id: 'clock', name: 'Clock', img: clockImg },
  { id: 'glove', name: 'Glove', img: gloveImg },
  { id: 'diary', name: 'Diary', img: diaryImg },
  { id: 'ring', name: 'Ring', img: ringImg },
];

const START_TIME = 150;
const BLACKOUT_ROUND = 5;
const BLACKOUT_TIME_BONUS = 30;
const BLACKOUT_TRANSITION_MS = 1200;

function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

const EndlessMode = () => {
  const navigate = useNavigate();

  const [started, setStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);

  const [round, setRound] = useState(1);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(START_TIME);

  const [sceneObjects, setSceneObjects] = useState([]);
  const [targets, setTargets] = useState([]);
  const [foundIds, setFoundIds] = useState([]);

  const [openSettings, setOpenSettings] = useState(false);
  const [showLosePopup, setShowLosePopup] = useState(false);
  const [mousePos, setMousePos] = useState({ x: -200, y: -200 });

  // blackout is purely visual state — it never reads or writes timeLeft directly
  // (the one exception being the explicit one-time +30s bonus applied below)
  const [blackoutActive, setBlackoutActive] = useState(false);
  const [blackoutEntering, setBlackoutEntering] = useState(false);

  const lockedAudio = useRef(null);
  const settingsAudio = useRef(null);
  const blackoutTimeoutRef = useRef(null);

  // tracks whether blackout has already fired this game — survives re-renders
  // without itself causing one, and resets only inside startGame()
  const blackoutTriggeredRef = useRef(false);

  useEffect(() => {
    lockedAudio.current = new Audio(lockedSound);
    settingsAudio.current = new Audio(settingsSound);
  }, []);

  const playLockedSound = () => {
    if (!lockedAudio.current) return;
    lockedAudio.current.currentTime = 0;
    lockedAudio.current.play().catch(() => {});
  };

  const playSettingsSound = () => {
    if (!settingsAudio.current) return;
    settingsAudio.current.currentTime = 0;
    settingsAudio.current.play().catch(() => {});
  };

  const handleMouseMove = useCallback((e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    if (touch) setMousePos({ x: touch.clientX, y: touch.clientY });
  }, []);

  useEffect(() => {
    if (!started || gameOver) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [started, gameOver, handleMouseMove, handleTouchMove]);

  // BLACKOUT — fires exactly once per game, the first time round reaches the
  // threshold. blackoutTriggeredRef guarantees it cannot re-fire even if the
  // player's round count fluctuates or the effect re-runs. Grants a one-time
  // +30s time bonus the moment the transition completes and the flashlight
  // effect actually engages.
  useEffect(() => {
    if (
      round >= BLACKOUT_ROUND &&
      started &&
      !gameOver &&
      !blackoutTriggeredRef.current
    ) {
      blackoutTriggeredRef.current = true;
      setBlackoutEntering(true);

      blackoutTimeoutRef.current = setTimeout(() => {
        setBlackoutActive(true);
        setBlackoutEntering(false);
        setTimeLeft((prev) => prev + BLACKOUT_TIME_BONUS);
      }, BLACKOUT_TRANSITION_MS);
    }

    return () => clearTimeout(blackoutTimeoutRef.current);
  }, [round, started, gameOver]);

  const newRound = () => {
    const objects = shuffle(OBJECTS).map((obj) => ({
      ...obj,
      top: Math.random() * 80 + 5,
      left: Math.random() * 88 + 4,
    }));
    setSceneObjects(objects);
    setTargets(objects.slice(0, 5));
    setFoundIds([]);
  };

  const startGame = () => {
    clearTimeout(blackoutTimeoutRef.current);
    blackoutTriggeredRef.current = false;

    setScore(0);
    setRound(1);
    setTimeLeft(START_TIME);
    setGameOver(false);
    setShowLosePopup(false);
    setStarted(true);
    setBlackoutActive(false);
    setBlackoutEntering(false);
    setMousePos({ x: -200, y: -200 });
    newRound();
  };

  // COUNTDOWN — the single source of truth for timeLeft, aside from the
  // one-time blackout bonus above and the reset inside startGame().
  // Depends only on started/gameOver, so nothing about blackout state can
  // pause or restart it.
  useEffect(() => {
    if (!started || gameOver) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setGameOver(true);
          setShowLosePopup(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [started, gameOver]);

  const handleClick = (obj) => {
    playLockedSound();

    const isTarget = targets.some((t) => t.id === obj.id);
    if (!isTarget || foundIds.includes(obj.id)) return;

    const updated = [...foundIds, obj.id];
    setFoundIds(updated);

    if (updated.length === targets.length) {
      setScore((s) => s + 1);
      setRound((r) => r + 1);
      setTimeout(newRound, 600);
    }
  };

  const minutes = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const seconds = String(timeLeft % 60).padStart(2, '0');
  const isWarning = timeLeft <= 60;
  const fillPercent = (timeLeft / START_TIME) * 100;

  const flashlightStyle =
    blackoutActive
      ? {
          '--fx': `${mousePos.x}px`,
          '--fy': `${mousePos.y}px`,
        }
      : {};

  if (!started) {
    return (
      <div className="endlessroot">
        <div className="endlesscard">
          <h1 className="endlesstitle">LOST &amp; FOUND</h1>
          <p className="endlesssubtitle">
            Find the hidden objects before time runs out.
          </p>
          <button className="endlessbtn" onClick={() => { playLockedSound(); startGame(); }}>
            START
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`endlessroot${blackoutActive ? ' blackout-active' : ''}${blackoutEntering ? ' blackout-entering' : ''}`}
      style={flashlightStyle}
    >
      {(blackoutActive || blackoutEntering) && (
        <div className="flashlight-overlay" />
      )}

      {blackoutEntering && (
        <div className="blackout-banner">⚡ THE LIGHTS WENT OUT ⚡</div>
      )}

      <div className="room1nav" style={{ position: 'relative', zIndex: 10 }}>
        <div />
        <div className="roomtimer">
          <span className="timerlabel">TIME REMAINING</span>
          <div className="timerdiv">
            <span className={`timerdigits ${isWarning ? 'timerwarning' : ''}`}>
              {minutes}:{seconds}
            </span>
          </div>
          <div className="timerbar">
            <div
              className="timerbartill"
              style={{
                width: `${fillPercent}%`,
                background: isWarning ? '#ff4444' : '#D4AF37',
              }}
            />
          </div>
        </div>
        <div
          className="settings"
          onClick={() => {
            playSettingsSound();
            setOpenSettings(true);
          }}
        />
      </div>

      <div className="endlessstats" style={{ zIndex: 10 }}>
        <span>ROUND {round}</span>
        <span>SCORE {score}</span>
        {round >= BLACKOUT_ROUND && (
          <span className="blackout-badge">🕯 BLACKOUT</span>
        )}
      </div>

      <div className="endlesstargets">
        {targets.map((t) => (
          <span
            key={t.id}
            className={`endlesschip ${foundIds.includes(t.id) ? 'found' : ''}`}
          >
            {t.name}
          </span>
        ))}
      </div>

      <div className="endlessscene">
        {sceneObjects.map((obj) => (
          <img
            key={obj.id}
            src={obj.img}
            alt={obj.name}
            className={`endlessobj ${foundIds.includes(obj.id) ? 'hidden' : ''}`}
            style={{
              top: `${obj.top}%`,
              left: `${obj.left}%`,
            }}
            onClick={() => handleClick(obj)}
          />
        ))}
      </div>

      <Settings
        open={openSettings}
        onClose={() => {
          playSettingsSound();
          setOpenSettings(false);
        }}
        onPlaySound={playSettingsSound}
        onNavigate={navigate}
      />

      <Lost open={showLosePopup} onRetry={startGame} />
    </div>
  );
};

export default EndlessMode;